import React from 'react'
import styled from 'styled-components'

import ActionToggle from 'live/components/ui/action-toggle'

import i18n from 'live/services/i18n'

import { ImageLogColorScale } from 'live-charts/types/image-log'

import { ImageLogConfig } from '../../../types/image-log'
import { useSelectedAssetLookup } from '../../../utils/hooks/useSelectedAssetLookup'
import AxesSizeConfig from '../common/AxesSizeConfig'
import NumericField from '../common/NumericField'
import ColorScaleSelector from './ColorScaleSelector'
import WellSelector from './WellSelector'
import ImageTypeSelector from './ImageTypeSelector'

type ConfigFormProps = {
    changeConfig: (field: string, value: unknown) => void
    changeLayoutConfig: (field: string, value: unknown) => void
    widgetConfig: LiveWidgetConfig<ImageLogConfig>
    isEditor: boolean
}

const ConfigForm = ({
    changeConfig,
    changeLayoutConfig,
    widgetConfig,
    isEditor
}: ConfigFormProps): React.ReactElement => {
    const selectedAsset = useSelectedAssetLookup()

    const useDefaultMinMax = widgetConfig?.useDefaultMinMax ?? true
    const scaleMin = widgetConfig?.scaleMin
    const scaleMax = widgetConfig?.scaleMax

    const interpolate = widgetConfig?.interpolate ?? true
    const numberOfVisibleAxes = widgetConfig?.numberOfVisibleAxes ?? 2
    const hideYAxisValues = widgetConfig?.hideYAxisValues ?? false
    const hideXAxis = widgetConfig?.hideXAxis ?? false

    const imageType = widgetConfig?.imageType
    const colorScale = widgetConfig?.colorScale ?? ImageLogColorScale.YellowOrangeBrown
    const verticalEnabled = widgetConfig.verticalEnabled

    return (
        <StyledConfigForm isEditor={isEditor}>
            <Content>
                {isEditor && (
                    <WellSelector
                        isEditor={isEditor}
                        changeConfig={changeConfig}
                        widgetConfig={widgetConfig}
                        value={selectedAsset}
                    />
                )}

                {selectedAsset && (
                    <>
                        <ImageTypeSelector selectedWell={selectedAsset} imageType={imageType} changeConfig={changeConfig} />
                        <ColorScaleSelector value={colorScale} onChange={value => changeConfig('colorScale', value)} />
                    </>
                )}

                <ActionToggle
                    active={useDefaultMinMax}
                    label={i18n('Use default min/max')}
                    onChange={() => changeConfig('useDefaultMinMax', !useDefaultMinMax)}
                    className="action-toggle-item"
                />
                {!useDefaultMinMax && (
                    <Divider className='space-item'>
                        <MinMaxContainer>
                            <NumericField
                                className="dropdown-toggler--cancel-drag"
                                value={scaleMin}
                                onChange={value => changeConfig('scaleMin', value)}
                                label={i18n('Min')}
                            />
                            <NumericField
                                className="dropdown-toggler--cancel-drag"
                                value={scaleMax}
                                onChange={value => changeConfig('scaleMax', value)}
                                label={i18n('Max')}
                            />
                        </MinMaxContainer>
                    </Divider>
                )}
                <ActionToggle
                    active={interpolate}
                    label={i18n('Interpolate')}
                    onChange={() => changeConfig('interpolate', !interpolate)}
                    className="action-toggle-item"
                />
                <Divider className='space-item'>
                    <AxesSizeConfig
                        label={i18n('Size of axes area')}
                        value={numberOfVisibleAxes}
                        onChange={value => changeLayoutConfig('numberOfVisibleAxes', value)}
                    />
                </Divider>
                <Divider>
                    <label className='space-item'>{i18n('Hide values')}</label>
                    <ActionToggle
                        active={hideXAxis}
                        label={i18n((verticalEnabled ? 'Y' : 'X') + " " + "axis")}
                        onChange={() => changeLayoutConfig('hideXAxis', !hideXAxis)}
                        className="action-toggle-item"
                    />
                    <ActionToggle
                        active={hideYAxisValues}
                        label={i18n((verticalEnabled ? 'X' : 'Y') + " " + "axis")}
                        onChange={() => changeLayoutConfig('hideYAxisValues', !hideYAxisValues)}
                        className="action-toggle-item"
                    />
                </Divider>
            </Content>
        </StyledConfigForm>
    )
}

export default ConfigForm

const Content = styled.div`
    padding: 7px;
    padding-top: 0;
    .action-toggle-item + label {
        margin-right: 10px;
    }
    .space-item {
        margin-top: 20px;
        margin-bottom: 10px;
    }
    .form-group {
        margin-bottom: 14px;
    }
`
const Divider = styled.div`
    margin-top: 7px;
`
export const StyledConfigForm = styled.div<{ isEditor: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 12px;
    max-height: 100%;
    margin-right: 5px;
    margin-left: 5px;
    margin-top: ${({ isEditor }) => (isEditor ? 50 : 10)}px;

    > div.form-group {
        margin-bottom: 15px;
    }

    .input-field {
        display: flex;

        > div {
            border: none;
            background-color: var(--gray-200);
        }
    }
`

const MinMaxContainer = styled.div`
    display: flex;
    gap: 10px;
`
